import {
    FormControl,
    InputLabel,
    MenuItem,
    Stack,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import React, { FC } from 'react'
import { Field, FormRenderProps } from 'react-final-form'
import { RegisterSubmitEvent } from '../../Containers/RootContainer'
import { ReservationState } from '../../Redux/reservationRedux'
import CheckboxWrapper from '../Form/Checkbox'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../Redux'
import SelectWrapper from '../Form/Select'
import { PrimaryButton } from '../Button'
import {ATJ_VEHICLE_TYPE_CAR, atjVehicleTypes} from '../../Utils/const'
import RegisterNumberField from "../RegisterNumberField";

const useStyles = makeStyles()((theme: Theme) => ({
    buttonText: {
        color: theme.palette.grey['100'],
    },
    container: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    errorText: {
        color: theme.palette.error.main,
    },
    form: {
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    formField: {
        '&:nth-of-type(1)': {
            marginTop: 0,
        },
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        width: 250,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    nextButton: {
        borderRadius: 24,
        fontSize: theme.typography.pxToRem(16),
        height: 40,
        width: 250,
        minWidth: 150,
        marginTop: theme.spacing(3),
    },
    vehicleType: {
        fontSize: theme.typography.pxToRem(18),
    },
    textField: {
        width: 250,
        paddingRight: '0 !important',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
}))

interface RegisterFormProps
    extends FormRenderProps<RegisterSubmitEvent, Partial<RegisterSubmitEvent>> {
    registerInfo?: ReservationState['registerInfo']
    validateRegisterNumber: any
    fetching: boolean
    renderSubmitRegisterPlate: () => JSX.Element
    inputId: string
}

export const RegisterForm: FC<RegisterFormProps> = ({
    inputId,
    handleSubmit,
    values,
    renderSubmitRegisterPlate,
}) => {
    const { classes } = useStyles()
    const { t } = useTranslation()
    const fuels = useSelector((state: RootState) => state.ui.settings?.fuels)
    const vehicleGroups = useSelector((state: RootState) => state.ui.settings?.vehicleGroups)
    const vehicleTypes = useSelector((state: RootState) => state.ui.settings?.booking.options.vehicleTypes)
    const allowRegistrationInspection = useSelector((state: RootState) => state.ui.settings?.booking.options.allowRegistrationInspection)

    const vehicleTypeOptions = vehicleTypes && (vehicleTypes.length > 1 || (vehicleTypes.length === 1 && vehicleTypes[0] !== ATJ_VEHICLE_TYPE_CAR)) ? atjVehicleTypes.filter(o => vehicleTypes.includes(o.id)) : null

    const classesProps = {
        classes: { root: classes.textField },
    }

    return (
        <form
            onSubmit={handleSubmit}
            className={classes.form}
            id="registerForm"
        >
            {!values.unregistered &&
                <Stack className={classes.container} direction="row" spacing={4}>
                    {vehicleTypeOptions && <Field name="vehicleType" type="text" defaultValue={vehicleTypeOptions[0].id} className={classes.vehicleType}>
                        {(props) => (
                            <FormControl variant="standard" className={classes.formField}>
                                <InputLabel htmlFor="vehicle-type">
                                    {t('common.vehicleType')}
                                </InputLabel>
                                <SelectWrapper
                                    {...props}
                                    {...classesProps}
                                    inputProps={{
                                        name: 'vehicle-type',
                                        id: 'vehicle-type-select',
                                    }}
                                >
                                    {vehicleTypeOptions?.map((item: any) => (
                                        <MenuItem value={item.id} key={item.name}>
                                            {t('common.' + item.name)}
                                        </MenuItem>
                                    ))}
                                </SelectWrapper>
                            </FormControl>
                        )}
                    </Field>}
                    <RegisterNumberField id={inputId} disabled={values.unregistered} showStars/>
                    {renderSubmitRegisterPlate()}
                </Stack>
            }
            {allowRegistrationInspection &&
                <Field
                    name="unregistered"
                    defaultValue={false}
                    type="checkbox"
                >
                    {(props) => (
                        <>
                            <CheckboxWrapper
                                {...props}
                                label={t(
                                    'rootContainer.unregisteredVehicle',
                                )}
                            />
                        </>
                    )}
                </Field>
            }
            {values.unregistered &&
                <>
                    <Field
                        name="fuel"
                        type="text"
                    >
                        {(props) => (
                            <FormControl variant="standard" required className={classes.formField}>
                                <InputLabel htmlFor="fuel-select">
                                    {t('common.fuel')}
                                </InputLabel>
                                <SelectWrapper
                                    {...props}
                                    {...classesProps}
                                    inputProps={{
                                        name: 'fuel',
                                        id: 'fuel-select',
                                    }}
                                >
                                    {fuels?.map((item: any) => (
                                        <MenuItem value={item.id} key={item.id}>
                                            {t('common.fuel_' + item.name)}
                                        </MenuItem>
                                    ))}
                                </SelectWrapper>
                            </FormControl>
                        )}
                    </Field>
                    <Field name="vehicleGroup" type="text">
                        {(props) => (
                            <FormControl variant="standard" required className={classes.formField}>
                                <InputLabel htmlFor="vehicle-group">
                                    {t('common.vehicleGroup')}
                                </InputLabel>
                                <SelectWrapper
                                    {...props}
                                    {...classesProps}
                                    inputProps={{
                                        name: 'vehicle-group',
                                        id: 'vehicle-group-select',
                                    }}
                                >
                                    {vehicleGroups?.map((item: any) => (
                                        <MenuItem value={item.name} key={item.name}>
                                            {t('common.' + item.name)}
                                        </MenuItem>
                                    ))}
                                </SelectWrapper>
                            </FormControl>
                        )}
                    </Field>
                    <PrimaryButton
                        id="next-vehicle-new"
                        type="submit"
                        className={classes.nextButton}
                        disabled={!values.fuel || !values.vehicleGroup}
                    >
                        {t('common.next')}
                    </PrimaryButton>
                </>
            }
        </form >
    )
}
